import type { ReactNode, SelectHTMLAttributes } from 'react';
import { useId } from 'react';

import focus from './focus.module.css';
import styles from './Select.module.css';

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  /** What the control is for. Always present; `labelHidden` decides whether it is drawn. */
  readonly label: string;
  /**
   * Hides the label visually and leaves it to a screen reader. For a control whose current value already
   * says what it is — `LED-05`'s "August", where a visible "Month" beside it would say it twice.
   */
  readonly labelHidden?: boolean;
  readonly children: ReactNode;
}

/**
 * A native `select`, styled.
 *
 * Native on purpose: a phone renders it as the platform picker somebody already knows how to use, and it
 * arrives with keyboard handling, type-ahead and a screen reader contract that a custom listbox would have
 * to reimplement and would get subtly wrong. What is styled is the box and the arrow; the popup belongs to
 * the platform.
 *
 * The arrow is drawn with gradients rather than set as a `▾` character, so it does not move when the
 * font falls back.
 *
 * Screens: `LED-05` (`FEAT-011 §2.1`).
 */
export function Select({
  label,
  labelHidden,
  children,
  className,
  id,
  ...rest
}: SelectProps): React.JSX.Element {
  const generated = useId();
  const selectId = id ?? generated;

  return (
    <div className={[styles.field, className ?? ''].filter(Boolean).join(' ')}>
      <label className={labelHidden ? styles.hidden : styles.label} htmlFor={selectId}>
        {label}
      </label>
      <select id={selectId} className={[styles.select, focus.focusable].join(' ')} {...rest}>
        {children}
      </select>
    </div>
  );
}
